import React from 'react';
import { motion } from 'motion/react';
import { Activity, AlertTriangle, ShieldAlert, Zap, RotateCcw, BellRing, History, Search } from 'lucide-react';

export default function IncidentResponsePolicy() {
  const lastUpdated = "April 21, 2026";

  const responseSteps = [
    {
      title: "Detection & Monitoring",
      description: "Security events across our infrastructure are monitored 24/7 and logged in a tamper-proof environment for immediate forensic accessibility.",
      icon: Search
    },
    {
      title: "Severity Assessment",
      description: "Every security event is automatically triaged and assessed based on business impact, data sensitivity, and operational risk factors.",
      icon: Activity
    },
    {
      title: "Threat Containment",
      description: "When a potential threat is identified, immediate software-defined isolation protocols are triggered to contain the threat and prevent lateral movement.",
      icon: ShieldAlert
    },
    {
      title: "Active Mitigation",
      description: "Our security team executes pre-defined playbooks to neutralize threats and patch vulnerabilities in real-time, minimizing potential downtime.",
      icon: Zap
    },
    {
      title: "Critical Recovery",
      description: "Affected systems are securely restored from verified, malware-free snapshots, with rigorous integrity checks performed before returning to live status.",
      icon: RotateCcw
    },
    {
      title: "Strategic Notification",
      description: "Transparency is paramount. Clients are notified through secure channels regarding incidents that impact their environments or data integrity.",
      icon: BellRing
    }
  ];

  return (
    <div className="min-h-screen bg-background pt-32 pb-20">
      <div className="container mx-auto px-4 md:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto space-y-12"
        >
          {/* Header */}
          <div className="space-y-4 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 border border-accent/10 text-accent text-[10px] font-bold uppercase tracking-[0.3em]">
              Rapid Response Protocol
            </div>
            <h1 className="text-4xl md:text-6xl font-display font-bold tracking-tight">
              Incident Response <span className="text-accent">Policy</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Agrandintl maintains aggressive, battle-tested procedures to detect, neutralize, and recover from security incidents with minimal business disruption.
            </p>
          </div>

          {/* Monitoring Status "Dashboard" feel */}
          <div className="glass-card p-6 rounded-3xl border-white/5 bg-white/[0.01] flex flex-wrap items-center justify-center gap-8 md:gap-16">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-[10px] uppercase font-bold tracking-widest text-muted-foreground">Detection Active</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-[10px] uppercase font-bold tracking-widest text-muted-foreground">Logging Operational</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-[10px] uppercase font-bold tracking-widest text-muted-foreground">SOC Team Standby</span>
            </div>
          </div>

          {/* Response Lifecycle */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {responseSteps.map((step, idx) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: idx * 0.05 }}
                className="glass-card p-8 rounded-3xl border-white/5 bg-white/[0.01] relative overflow-hidden group"
              >
                <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                  <step.icon className="w-24 h-24" />
                </div>
                <div className="space-y-4 relative z-10">
                  <div className="w-12 h-12 rounded-2xl bg-accent/10 flex items-center justify-center text-accent">
                    <step.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-display font-bold">{step.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Closing Commitment */}
          <div className="glass-card p-8 rounded-[2rem] border-white/5 bg-white/[0.01] text-center border-t-accent/20">
            <div className="flex justify-center mb-6">
              <AlertTriangle className="w-10 h-10 text-accent" />
            </div>
            <h2 className="text-2xl font-display font-bold mb-4">Preventive Invariant</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto italic">
              "Post-incident analysis is mandatory for every high-severity event. We don't just recover; we evolve—implementing preventive measures to ensure the same threat never succeeds twice."
            </p>
          </div>

          {/* Footer Metadata */}
          <div className="pt-12 border-t border-white/5 text-center">
            <div className="flex justify-center gap-4 mb-6">
              {["ISO 27001 ALIGNED", "NIST CSF READY", "SOC 2 TYPE II"].map((cert) => (
                <div key={cert} className="px-3 py-1 rounded bg-white/5 text-[8px] font-bold tracking-tighter text-muted-foreground uppercase border border-white/10">
                  {cert}
                </div>
              ))}
            </div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground/60">
              Protocol: AG-IR-2026 | Revision v4.2 | {lastUpdated}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
